// Gentle one-time entrance for .mond-reveal elements.
(() => {
  const els = document.querySelectorAll('.mond-reveal');
  if (!els.length) return;
  if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) {
    els.forEach((el) => el.classList.add('is-visible'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px' }
  );
  els.forEach((el) => io.observe(el));
})();

// Any link pointing at #cerez-tercihleri reopens Shopify's cookie preferences dialog.
document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href*="#cerez-tercihleri"]');
  if (!link || !window.privacyBanner?.showPreferences) return;
  event.preventDefault();
  window.privacyBanner.showPreferences();
});

// Gift wrap + gift note in the cart summary (snippets/mond-gift-wrap.liquid).
// Delegated listeners: the cart drawer / cart page are morphed after every cart change, so nothing binds to nodes.
(() => {
  const NOTE_KEY = 'Hediye notu';
  const root = () => window.Shopify?.routes?.root || '/';
  const jsonPost = async (path, body) => {
    const response = await fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Sepet güncellenemedi');
    return data;
  };
  const getCart = () => fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  const cartSectionIds = () =>
    [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId);
  const setToggles = (state) =>
    document.querySelectorAll('[data-mond-gift-toggle]').forEach((el) => {
      if (state.disabled !== undefined) el.disabled = state.disabled;
      if (state.checked !== undefined) el.checked = state.checked;
    });

  async function toggleGift(input) {
    const box = input.closest('[data-mond-gift]');
    if (!box) return;
    const variantId = Number(box.dataset.variantId);
    const add = input.checked;
    const error = box.querySelector('.mond-gift__error');
    const note = box.querySelector('.mond-gift__note');
    if (note) note.hidden = !add;
    if (error) error.hidden = true;
    setToggles({ disabled: true, checked: add });

    let CartLinesUpdateEvent = null;
    try {
      ({ CartLinesUpdateEvent } = await import('@shopify/events'));
    } catch (e) {
      /* fall back to a reload below */
    }

    const sectionIds = cartSectionIds();
    let deferred = null;
    try {
      let lineKey = String(variantId);
      if (!add) {
        const before = await getCart();
        lineKey = before.items.find((item) => item.variant_id === variantId)?.key || lineKey;
      }
      if (CartLinesUpdateEvent && sectionIds.length) {
        deferred = CartLinesUpdateEvent.createPromise();
        // The theme's cart-items-component, cart-drawer and cart-icon listen for this and morph the cart sections.
        box.dispatchEvent(
          new CartLinesUpdateEvent({
            action: add ? 'add' : 'remove',
            context: 'cart',
            lines: [add ? { merchandiseId: String(variantId), quantity: 1 } : { id: lineKey, quantity: 0 }],
            promise: deferred.promise,
          })
        );
      }
      const sectionsParams = { sections: sectionIds.join(','), sections_url: window.location.pathname };
      const result = add
        ? await jsonPost('cart/add.js', { items: [{ id: variantId, quantity: 1 }], ...sectionsParams })
        : await jsonPost('cart/change.js', { id: lineKey, quantity: 0, ...sectionsParams });
      const cart = add ? await getCart() : result;

      if (deferred) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            sections: result.sections || undefined,
            items: cart.items,
            itemCount: cart.item_count,
            source: 'mond-gift-wrap',
            didError: false,
          },
        });
      } else {
        window.location.reload();
      }
    } catch (e) {
      deferred?.reject(e);
      setToggles({ checked: !add });
      if (note) note.hidden = add;
      if (error) {
        error.textContent = 'Hediye paketi güncellenemedi, lütfen tekrar dene.';
        error.hidden = false;
      }
      console.warn('[mond-gift-wrap]', e);
    } finally {
      setToggles({ disabled: false });
    }
  }

  let noteTimer;
  function saveNote(textarea) {
    const value = textarea.value.slice(0, 200);
    document.querySelectorAll('[data-mond-gift-note]').forEach((el) => {
      if (el !== textarea) el.value = value;
    });
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      jsonPost('cart/update.js', { attributes: { [NOTE_KEY]: value } }).catch((e) =>
        console.warn('[mond-gift-wrap] note', e)
      );
    }, 600);
  }

  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('[data-mond-gift-toggle]');
    if (input) toggleGift(input);
  });
  document.addEventListener('input', (event) => {
    const textarea = event.target.closest?.('[data-mond-gift-note]');
    if (textarea) saveNote(textarea);
  });
})();

// Native <dialog> openers (snippets/mond-size-guide.liquid). showModal() traps focus and handles Esc;
// focus goes back to the opener on close.
document.addEventListener('click', (event) => {
  const opener = event.target.closest?.('[data-mond-dialog-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.mondDialogOpen);
    if (dialog instanceof HTMLDialogElement && !dialog.open) {
      dialog.showModal();
      dialog.querySelector('[data-mond-dialog-close]')?.focus();
      dialog.addEventListener('close', () => opener.focus(), { once: true });
    }
    return;
  }
  const closer = event.target.closest?.('[data-mond-dialog-close]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }
  // Click on the backdrop (the dialog element itself, outside its content box) closes it.
  if (event.target instanceof HTMLDialogElement && event.target.matches('.mond-size__dialog')) {
    const r = event.target.getBoundingClientRect();
    const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
    if (!inside) event.target.close();
  }
});

// Announcement bar: slides hidden with aria-hidden="true" become inert, so their links leave the tab order
// (Lighthouse/axe "aria-hidden-focus"). Follows the slideshow as it flips aria-hidden.
(() => {
  const slides = document.querySelectorAll('.announcement-bar__slide');
  if (!slides.length || !('inert' in HTMLElement.prototype)) return;
  const sync = (slide) => {
    slide.inert = slide.getAttribute('aria-hidden') === 'true';
  };
  slides.forEach(sync);
  const observer = new MutationObserver((records) => records.forEach((record) => sync(record.target)));
  slides.forEach((slide) => observer.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] }));
})();

// Cart page mode (no drawer): after a successful add from a product page, card or quick-add, go to the cart page
// so the shopper sees recommendations, gift wrap and the note before checkout.
(async () => {
  if (document.querySelector('cart-drawer-component') || window.location.pathname.startsWith('/cart')) return;
  let StandardEvents = null;
  try {
    ({ StandardEvents } = await import('@shopify/events'));
  } catch (e) {
    return;
  }
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (event.action !== 'add' || event.context === 'cart') return;
    event.promise
      ?.then((result) => {
        if (result?.detail?.didError) return;
        window.location.href = window.Theme?.routes?.cart_url || '/cart';
      })
      .catch(() => {});
  });
})();

// Free-shipping nudge (cart page): on checkout, if the cart is a little short of the threshold, offer a few
// single-variant products that close the gap. Once per session; the shopper can always continue unchanged.
//
// Selection rule (so the same products don't show every time):
// 1. Pool: offerable products (sections/mond-nudge-data.liquid) not in the cart, priced from the gap up to
//    gap + 250 TL, so the add-on doesn't feel like a second purchase (same-title items skipped). If fewer than 3
//    fit, the 6 cheapest above the gap.
// 2. Weight: related to a cart item 3, best seller 2, other 1; ×0.6 if the cart already has that
//    type (prefer a complementary piece); ×0.15 if this visitor was shown it in an earlier nudge (last 18 kept).
// 3. Draw 3 by weighted random, one per product type while types last; shown cheapest first.
(() => {
  const SEEN_KEY = 'mondNudgeShown';
  const RECENT_KEY = 'mondNudgeRecent';
  const WINDOW = 25000;
  const money = (cents) => `${Math.round(cents / 100).toLocaleString('tr-TR')} TL`;

  const seen = () => {
    try {
      return sessionStorage.getItem(SEEN_KEY) === '1';
    } catch (e) {
      return false;
    }
  };
  const markSeen = () => {
    try {
      sessionStorage.setItem(SEEN_KEY, '1');
    } catch (e) {
      /* private mode: the nudge may show again, which is harmless */
    }
  };

  const recent = () => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch (e) {
      return [];
    }
  };
  const remember = (ids) => {
    try {
      const kept = recent().filter((id) => !ids.includes(id));
      localStorage.setItem(RECENT_KEY, JSON.stringify([...ids, ...kept].slice(0, 18)));
    } catch (e) {
      /* no storage: rotation still works, just without memory */
    }
  };

  /** Reads the JSON that sections/mond-nudge-data.liquid renders for the given URL. */
  const feed = async (url) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(String(response.status));
    const html = await response.text();
    const json = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-mond-nudge-feed]')?.textContent;
    return json ? JSON.parse(json) : {};
  };

  async function findCandidates(cart, gap) {
    const inCart = new Set(cart.items.flatMap((item) => [item.product_id, item.product_title]));
    const lines = cart.items.filter((item) => !/hediye-paketi/.test(item.handle));
    const cartTypes = new Set(lines.map((item) => item.product_type));
    const [all, ...related] = await Promise.all([
      feed('/collections/all?section_id=mond-nudge-data'),
      ...lines
        .slice(0, 2)
        .map((item) =>
          feed(`/recommendations/products?product_id=${item.product_id}&limit=10&intent=related&section_id=mond-nudge-data`).catch(() => ({}))
        ),
    ]);
    const relatedIds = new Set(related.flatMap((data) => data.related || []));
    const shownBefore = new Set(recent());

    const affordable = (all.products || []).filter((p) => !inCart.has(p.id) && !inCart.has(p.t) && p.p >= gap).sort((a, b) => a.p - b.p);
    let pool = affordable.filter((p) => p.p <= gap + WINDOW);
    if (pool.length < 3) pool = affordable.slice(0, 6);

    const source = (p) => (relatedIds.has(p.id) ? 'ilgili' : p.b ? 'cok-satan' : 'diger');
    const base = { ilgili: 3, 'cok-satan': 2, diger: 1 };
    const weight = (p) => base[source(p)] * (cartTypes.has(p.y) ? 0.6 : 1) * (shownBefore.has(p.id) ? 0.15 : 1);

    const picks = [];
    const usedTypes = new Set();
    let rest = pool;
    while (picks.length < 3 && rest.length) {
      const newType = rest.filter((p) => !usedTypes.has(p.y));
      const from = newType.length ? newType : rest;
      let ticket = Math.random() * from.reduce((sum, p) => sum + weight(p), 0);
      const pick = from.find((p) => (ticket -= weight(p)) <= 0) || from[from.length - 1];
      picks.push(pick);
      usedTypes.add(pick.y);
      rest = rest.filter((p) => p !== pick);
    }
    return picks
      .sort((a, b) => a.p - b.p)
      .map((p) => ({ id: p.id, title: p.t, url: p.u, image: p.i, variantId: p.v, price: p.p, source: source(p) }));
  }

  function render(dialog, products, gap) {
    dialog.querySelectorAll('[data-mond-nudge-gap]').forEach((el) => (el.textContent = money(gap)));
    const list = dialog.querySelector('[data-mond-nudge-list]');
    list.replaceChildren(
      ...products.map((product) => {
        const li = document.createElement('li');
        li.className = 'mond-nudge__item';
        li.innerHTML = `
          <a class="mond-nudge__media" href="${product.url}" target="_blank" rel="noopener"></a>
          <span class="mond-nudge__name"></span>
          <span class="mond-nudge__price">${money(product.price)}</span>
          <button type="button" class="button mond-nudge__add" data-variant-id="${product.variantId}">Ekle</button>`;
        if (product.image) {
          const img = document.createElement('img');
          img.src = product.image;
          img.alt = product.title;
          img.loading = 'lazy';
          img.width = 120;
          img.height = 150;
          li.querySelector('.mond-nudge__media').append(img);
        }
        li.querySelector('.mond-nudge__name').textContent = product.title;
        li.querySelector('.mond-nudge__add').dataset.title = product.title;
        return li;
      })
    );
    dialog.querySelector('.mond-nudge__offer').hidden = false;
    dialog.querySelector('.mond-nudge__done').hidden = true;
    dialog.querySelector('[data-mond-nudge-error]').hidden = true;
  }

  document.addEventListener(
    'click',
    async (event) => {
      const button = event.target.closest('button[name="checkout"]');
      if (!button || !window.location.pathname.startsWith('/cart')) return;
      const dialog = document.querySelector('dialog[data-mond-nudge]');
      const form = document.getElementById('cart-form');
      if (!dialog || !form || button.dataset.mondNudgeBypass === '1' || seen()) return;
      if (!form.checkValidity()) return; // let the browser point at the unticked consent box first

      event.preventDefault();
      event.stopImmediatePropagation();
      const proceed = () => {
        button.dataset.mondNudgeBypass = '1';
        dialog.dataset.proceeding = '1';
        if (dialog.open) dialog.close();
        form.requestSubmit(button);
      };

      try {
        const cart = await fetch('/cart.js').then((response) => response.json());
        const gap = Number(dialog.dataset.threshold) - cart.total_price;
        if (gap <= 0 || gap > Number(dialog.dataset.maxGap)) return proceed();
        const products = await findCandidates(cart, gap);
        if (!products.length) return proceed();

        render(dialog, products, gap);
        markSeen();
        remember(products.map((p) => p.id));
        dialog.showModal();
        window.Shopify?.analytics?.publish?.('mond_ship_nudge_shown', {
          gap,
          products: products.map((p) => p.id),
          sources: products.map((p) => p.source),
        });
        window.mondTrack?.('free_shipping_offer_view', {
          gap_tl: Math.round(gap / 100),
          offer_sources: products.map((p) => p.source).join(','),
        });

        dialog.onclick = async (clickEvent) => {
          const target = clickEvent.target;
          if (target === dialog || target.closest('[data-mond-nudge-close]')) return dialog.close();
          if (target.closest('[data-mond-nudge-proceed]')) return proceed();
          const add = target.closest('.mond-nudge__add');
          if (!add) return;
          add.disabled = true;
          try {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: [{ id: Number(add.dataset.variantId), quantity: 1, properties: { _kaynak: 'kargo-onerisi' } }],
              }),
            });
            if (!response.ok) throw new Error(String(response.status));
            window.Shopify?.analytics?.publish?.('mond_ship_nudge_added', { variant: add.dataset.variantId });
            window.mondTrack?.(
              'free_shipping_offer_add',
              { gap_tl: Math.round(gap / 100), item_name: add.dataset.title },
              { name: 'FreeShippingOfferAdd', custom: true }
            );
            dialog.querySelector('[data-mond-nudge-added]').textContent = add.dataset.title;
            dialog.querySelector('.mond-nudge__offer').hidden = true;
            dialog.querySelector('.mond-nudge__done').hidden = false;
            dialog.addEventListener('close', () => dialog.dataset.proceeding !== '1' && window.location.assign(window.location.pathname), { once: true });
          } catch (e) {
            add.disabled = false;
            dialog.querySelector('[data-mond-nudge-error]').hidden = false;
          }
        };
      } catch (e) {
        proceed();
      }
    },
    true
  );
})();

// Analytics bridge: site interactions the Google & YouTube and Facebook & Instagram apps don't report on their own
// (the apps already send page views, product/list views, search, cart and checkout events). gtag and fbq only exist
// once the visitor has accepted cookies, so nothing here is sent without consent. Events fired before the apps have
// loaded wait up to 10 s. Full map: data/analytics/event-map (GA4 name ↔ Meta name ↔ trigger).
(() => {
  const GA4_ID = 'G-TDM3G8PL1N';
  const pending = [];
  let settled = false;
  const has = () => ({ ga: typeof window.gtag === 'function', fb: typeof window.fbq === 'function' });
  const send = ({ ga4, params, meta }) => {
    const { ga, fb } = has();
    if (ga4 && ga) window.gtag('event', ga4, { send_to: GA4_ID, ...params });
    if (meta && fb) window.fbq(meta.custom ? 'trackCustom' : 'track', meta.name, meta.params || {});
  };
  const flush = () => {
    const { ga, fb } = has();
    if ((ga && fb) || settled) pending.splice(0).forEach(send);
  };
  window.mondTrack = (ga4, params = {}, meta = null) => {
    pending.push({ ga4, params, meta });
    flush();
  };
  let tries = 0;
  const timer = setInterval(() => {
    if (++tries >= 20) {
      settled = true;
      clearInterval(timer);
    }
    flush();
  }, 500);

  const track = window.mondTrack;
  const path = window.location.pathname;
  const pageType = () => window.ShopifyAnalytics?.meta?.page?.pageType || '';

  // Page-load outcomes
  const onReady = (fn) =>
    document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn, { once: true }) : fn();
  onReady(() => {
    if (document.querySelector('.email-signup__message .icon-success')) {
      track('generate_lead', { lead_source: 'bulten' }, { name: 'Lead', params: { content_name: 'Bülten' } });
    }
    if (document.querySelector('.contact-form__success')) {
      track('contact_form_submit', { form_name: 'iletisim' }, { name: 'Contact' });
    }
    if (/^404\b/.test(document.title)) {
      track('page_not_found', { page_path: path, page_referrer: document.referrer });
    }
    const term = new URLSearchParams(window.location.search).get('q');
    // The empty results page still shows suggested products, so read the count from the title ("… için 0 sonuç …").
    if (path.startsWith('/search') && term && /için 0 sonuç/.test(document.title)) {
      track('search_no_results', { search_term: term });
    }
  });

  // Clicks
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const sizeGuide = target.closest('[data-mond-dialog-open]');
      if (sizeGuide && document.getElementById(sizeGuide.dataset.mondDialogOpen)?.matches('.mond-size__dialog')) {
        track('size_guide_open', { page_path: path }, { name: 'SizeGuideOpen', custom: true });
        return;
      }

      const checkout = target.closest('button[name="checkout"]');
      if (checkout) {
        const form = checkout.form || document.getElementById('cart-form');
        if (form && !form.checkValidity()) track('checkout_blocked', { reason: 'sozlesme_onayi' });
        return;
      }

      const link = target.closest('a[href]');
      if (!link) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return;

      // Product chosen from a list (collection, search, recommendations, home sections)
      const card = link.closest('product-card, [data-product-id]');
      if (card?.dataset.productId && /\/products\//.test(url.pathname) && !link.closest('.product-information')) {
        const id = card.dataset.productId;
        const listed = (window.dataLayer || [])
          .filter((entry) => entry?.[1] === 'view_item_list')
          .flatMap((entry) => entry[2]?.items || [])
          .find((item) => String(item.id).includes(`_${id}_`));
        const list = card.closest('section, .shopify-section');
        const listName =
          (pageType() === 'collection' && document.querySelector('h1')?.textContent.trim()) ||
          list?.querySelector('h2, h3')?.textContent.trim() ||
          pageType() ||
          'liste';
        track('select_item', {
          item_list_name: listName.slice(0, 100),
          items: [listed ? { ...listed, item_id: listed.id, item_name: listed.name } : { item_id: `shopify_ZZ_${id}` }],
        });
        return;
      }

      // Blog article → shop
      if (path.startsWith('/blogs/') && /^\/(products|collections)\//.test(url.pathname)) {
        track('blog_to_shop_click', { link_url: url.pathname, article_path: path });
      }
    },
    true
  );

  // Gift wrap and gift note (snippets/mond-gift-wrap.liquid)
  let noteTracked = false;
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.matches('[data-mond-gift-toggle]')) {
      track(target.checked ? 'gift_wrap_add' : 'gift_wrap_remove', { value: 50, currency: 'TRY' });
    } else if (target.matches('[data-mond-gift-note]') && target.value.trim() && !noteTracked) {
      noteTracked = true;
      track('gift_note_add', { note_length: target.value.trim().length });
    }
  });
})();
